import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { SearchIcon } from 'src/components/icons';
import { PortalConfigContext } from 'src/context';
import Logo, { LogoProps } from 'src/legacy/components/Logo';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { DividersAndCardBorders, GraySmall } from 'src/theme/colors';
import { LayoutPreviewShadow } from 'src/theme/shadows';

import {
  LayoutClasses,
  SidebarClasses,
  SidebarItem,
  SidebarItemClasses,
} from './layoutPreviewTypes';
import { SidebarItemPreview } from './SidebarItemPreview';

interface BaseLayoutPreviewProps {
  children?: React.ReactNode;
  items: Array<SidebarItem>;
  classes?: SidebarItemClasses & SidebarClasses & LayoutClasses;
  logoProps?: LogoProps;
  noPageTitle?: boolean;
}

const useStyles = makeStyles()((theme) => ({
  layoutContainer: {
    display: 'flex',
  },
  sidebarContainer: {
    backgroundColor: theme.palette.primary.main,
    borderTopLeftRadius: theme.shape.borderRadius,
    boxSizing: 'border-box',
    padding: theme.spacing(1),
    width: 132,
  },
  sidebarItemsWrapper: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  layoutContent: {
    boxShadow: LayoutPreviewShadow,
  },
  searchBar: {
    borderBottom: `1px solid ${DividersAndCardBorders}`,
    display: 'flex',
    alignItems: 'center',
    height: '40px',
    padding: theme.spacing(1, 2),
  },
  searchIcon: {
    color: GraySmall,
    fontSize: 8,
  },
  searchText: {
    color: GraySmall,
    marginLeft: theme.spacing(0.75),
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  layoutBody: {
    padding: theme.spacing(2),
  },
  pageTitleWrapper: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: theme.spacing(3.5),
  },
}));

export const LayoutPreview: React.FC<BaseLayoutPreviewProps> = ({
  items,
  classes,
  logoProps,
  children,
  noPageTitle = false, // used to not show the page title
}) => {
  const activeItem = items.find((item) => item.active);
  const { classes: defaultClasses, cx } = useStyles();
  const portalConfig = React.useContext(PortalConfigContext);

  return (
    <div
      className={cx(defaultClasses.layoutContainer, classes?.layoutContainer)}
    >
      <div className={cx(defaultClasses.sidebarContainer, classes?.sidebar)}>
        <div className={defaultClasses.sidebarHeader}>
          <Logo
            iconImagePreviewUrl={portalConfig.assets.logo.iconImageUrl}
            portalNamePreview={portalConfig.name}
            isPreviewLogo
            {...logoProps}
          />
        </div>
        <div className={defaultClasses.sidebarItemsWrapper}>
          {items.map((item) => (
            <SidebarItemPreview
              key={item.title}
              item={item}
              classes={{
                itemActive: classes?.itemActive,
                item: classes?.item,
                notificationBadge: classes?.notificationBadge,
              }}
            />
          ))}
        </div>
      </div>
      <div className={cx(defaultClasses.layoutContent, classes?.layoutContent)}>
        <div className={defaultClasses.searchBar}>
          <SearchIcon className={defaultClasses.searchIcon} />
          <BaseTypography
            fontType="13Medium"
            className={defaultClasses.searchText}
          >
            Search...
          </BaseTypography>
        </div>
        <div className={cx(defaultClasses.layoutBody, classes?.layoutBody)}>
          {!noPageTitle && (
            <div className={defaultClasses.pageTitleWrapper}>
              <BaseTypography fontType="24Medium">
                {activeItem?.title}
              </BaseTypography>
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};
